import { useEffect, useRef, useState } from 'react';
import { hasOpenDialog } from '@client/helpers/dom.helpers';
import {
  collectProjectFilesFromDataTransfer,
  type ProjectFileClientUpload,
} from '@client/helpers/project-file-upload-normalize.helpers';

const isFileDrag = (event: DragEvent): boolean =>
  Boolean(event.dataTransfer?.types.includes('Files'));

/** Page-wide FFU drops, including headers and maps, while dialogs keep their own drops. */
export const useFfuFileDrop = ({
  enabled,
  onFiles,
  onError,
}: {
  enabled: boolean;
  onFiles: (uploads: ProjectFileClientUpload[]) => void;
  onError: () => void;
}): boolean => {
  const [dragging, setDragging] = useState(false);
  const callbacks = useRef({ onFiles, onError });
  callbacks.current = { onFiles, onError };

  useEffect(() => {
    if (!enabled) return;
    let depth = 0;
    const reset = () => {
      depth = 0;
      setDragging(false);
    };
    const enter = (event: DragEvent) => {
      if (!isFileDrag(event) || hasOpenDialog()) return;
      depth += 1;
      setDragging(true);
    };
    const over = (event: DragEvent) => {
      if (!isFileDrag(event) || hasOpenDialog()) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
      setDragging(true);
    };
    const leave = (event: DragEvent) => {
      if (!isFileDrag(event)) return;
      depth -= 1;
      if (depth <= 0) reset();
    };
    const drop = (event: DragEvent) => {
      reset();
      if (!isFileDrag(event) || hasOpenDialog() || !event.dataTransfer) return;
      event.preventDefault();
      // Capture before the card/map handles the drop, so one drop starts one batch.
      event.stopPropagation();
      void collectProjectFilesFromDataTransfer(event.dataTransfer)
        .then((uploads) => callbacks.current.onFiles(uploads))
        .catch(() => callbacks.current.onError());
    };
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') reset();
    };
    window.addEventListener('dragenter', enter, true);
    window.addEventListener('dragover', over, true);
    window.addEventListener('dragleave', leave, true);
    window.addEventListener('drop', drop, true);
    window.addEventListener('dragend', reset);
    window.addEventListener('blur', reset);
    window.addEventListener('keydown', keydown);
    return () => {
      window.removeEventListener('dragenter', enter, true);
      window.removeEventListener('dragover', over, true);
      window.removeEventListener('dragleave', leave, true);
      window.removeEventListener('drop', drop, true);
      window.removeEventListener('dragend', reset);
      window.removeEventListener('blur', reset);
      window.removeEventListener('keydown', keydown);
      reset();
    };
  }, [enabled]);
  return enabled && dragging;
};
